import { useQuery } from '@tanstack/react-query';
import { ErrorNote, Spinner, ui } from '@/components/ui';
import { categoriesService, type CategoryChoice } from '@/services/api';

// Up to 3 categories (the API allows no more). The first is the main (primary) category.
const SLOTS = 3;

// Chosen slugs in order: [main, other, other]. Empty slots are ''.
export type CategorySlots = string[];

export const toChoices = (slots: CategorySlots): CategoryChoice[] =>
  slots.filter(Boolean).map((slug, i) => ({ slug, isPrimary: i === 0 }));

// The business's current categories, primary first, as picker slots.
export const toSlots = (categories: CategoryChoice[]): CategorySlots =>
  [...categories].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)).map((c) => c.slug);

// Category dropdowns: subcategories grouped under their main category ("Money & Insurance" →
// Banks, Loans, …), plus the main category itself for businesses that span it. The first is
// required; up to two more list the business elsewhere too.
export function CategoryPicker({
  value,
  onChange,
  disabled,
  error,
}: {
  value: CategorySlots;
  onChange: (next: CategorySlots) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  const q = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesService.list,
    staleTime: 60 * 60_000,
  });
  if (q.isPending) return <Spinner label="Loading categories" />;
  if (q.error) return <ErrorNote error={q.error} />;

  const set = (slot: number, slug: string) => {
    const next = Array.from({ length: SLOTS }, (_, i) => value[i] ?? '');
    next[slot] = slug;
    // Keep the chosen ones packed at the front, so "main" is always the first.
    onChange(next.filter(Boolean));
  };

  return (
    <div className={ui.stack}>
      {Array.from({ length: SLOTS }, (_, slot) => {
        // Show a slot once the one before it is filled.
        if (slot > 0 && !value[slot - 1]) return null;
        const id = `category-${slot}`;
        const current = value[slot] ?? '';
        const taken = new Set(value.filter((_, i) => i !== slot));
        return (
          <div key={id} className={ui.field}>
            <label htmlFor={id}>{slot === 0 ? 'Main category' : 'Also listed in (optional)'}</label>
            <select
              id={id}
              className={ui.input}
              value={current}
              disabled={disabled}
              onChange={(e) => {
                set(slot, e.target.value);
              }}
            >
              <option value="">{slot === 0 ? 'Choose what your business does' : 'None'}</option>
              {q.data.map((group) => (
                <optgroup key={group.slug} label={group.name}>
                  {taken.has(group.slug) ? null : (
                    <option value={group.slug}>{group.name} (general)</option>
                  )}
                  {group.children
                    .filter((c) => !taken.has(c.slug))
                    .map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.name}
                      </option>
                    ))}
                </optgroup>
              ))}
            </select>
            {slot === 0 ? (
              <span className={ui.hint}>
                Customers find you under this category. Add up to two more if you do several things.
              </span>
            ) : null}
            {slot === 0 && error ? <p className={ui.error}>{error}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
